import * as moment from "moment";
import { trace } from "owa-trace";
import RestClient from "sh-rest-client";
import { ChangeEntity, ChangeSource } from "sh-models";
import { DataServices, SmartScheduleDataService, AssignOpenShiftsPollingService, ECSConfigService, ECSConfigKey } from "sh-services";
import { TeamStore } from "sh-team-store";
import { JobStatus } from "sh-models/job/SmartScheduleGenerationJobStatusEntity";
import { setAssignOpenShiftsJob, clearAssignOpenShiftsJob, setShowFailureToast } from "./mutatorActions/assignOpenShiftsPollingActions";
import { UndoData, ReassignParams } from "./store/schema/AssignOpenShiftsPollingStateSchema";

/**
 * Builds a ChangeEntity for a successful Assign Open Shifts run.
 *
 * The entity carries a `getRevertTask` closure that — when invoked by the change store — performs the
 * undo (POST /undogeneratedschedule + view refresh) and returns a fresh ChangeEntity whose own
 * `getRevertTask` performs the redo (re-issues the assignment and starts polling). The two tasks are
 * mutually recursive so a user can undo → redo → undo any number of times.
 *
 * The redo path sets `skipTrackChanges` on the active job so the polling-success handler does not
 * also push an entity onto the stack — the entity returned by the redo task is the single record.
 */
export function buildAssignOpenShiftsChangeEntity(undoData: UndoData, reassignParams: ReassignParams): ChangeEntity {
    return new ChangeEntity(null, null, ChangeSource.AssignOpenShifts, buildUndoTask(undoData, reassignParams), null, null);
}

function buildUndoTask(undoData: UndoData, reassignParams: ReassignParams): () => Promise<ChangeEntity> {
    return async () => {
        // Defensive gate: the entity is only ever pushed onto the stack with the flag on, but flags can flip
        // mid-session. If the flag is off when undo fires, skip the REST work and return a no-op redo entity
        // so the stack mechanics stay consistent.
        if (!ECSConfigService.isECSFeatureEnabled(ECSConfigKey.EnableAssignOpenShifts)) {
            return new ChangeEntity(null, null, ChangeSource.AssignOpenShifts, buildRedoTask(undoData, reassignParams), null, null);
        }
        const tenantId = TeamStore()?.tenantId;
        try {
            await RestClient.undoGeneratedSchedule(tenantId, undoData.teamId, undoData.tagId, undoData.startDate, undoData.endDate);
        } catch (e) {
            // Re-throw so revertChanges' boundary catch drops the entry instead of advancing it onto
            // the redo stack — same convention every other revert task in the codebase follows.
            // The server did NOT revert the assignment, so pretending undo succeeded would let a
            // subsequent Redo try to re-assign a range that's still assigned, mismatching state.
            trace.warn(`assignOpenShifts undo: REST call failed: ${e?.message}`);
            throw e;
        }
        try {
            // Refresh the range that was actually assigned, not the user's current view.
            // Undo can fire long after the assign (hamburger menu, Ctrl+Z), so the view may be on a
            // different range by now. Refreshing the assigned range keeps the cache correct whether
            // the view is on it (MobX propagates to the grid) or off it (next navigation back sees
            // fresh data). `undoData.startDate`/`endDate` are UTC YYYY-MM-DD strings — parse with
            // `moment.utc` so the constructor doesn't reinterpret them in local time.
            await DataServices.TeamDataService.getDataInDateRange(
                undoData.teamId,
                moment.utc(undoData.startDate),
                moment.utc(undoData.endDate),
                true /* forceLoadFromNetwork */
            );
        } catch (e) {
            // The REST undo already succeeded server-side — refresh is best-effort. Throwing here
            // would wrongly cost the user the redo entry for an action that actually completed.
            // The local cache catches up on next navigation.
            trace.warn(`assignOpenShifts undo: refresh failed: ${e?.message}`);
        }
        return new ChangeEntity(null, null, ChangeSource.AssignOpenShifts, buildRedoTask(undoData, reassignParams), null, null);
    };
}

function buildRedoTask(undoData: UndoData, reassignParams: ReassignParams): () => Promise<ChangeEntity> {
    return async () => {
        // Defensive gate: see buildUndoTask. If the flag is off, no REST work, just return the next entity.
        if (!ECSConfigService.isECSFeatureEnabled(ECSConfigKey.EnableAssignOpenShifts)) {
            return new ChangeEntity(null, null, ChangeSource.AssignOpenShifts, buildUndoTask(undoData, reassignParams), null, null);
        }
        // Captured before the REST call so AssignCompleted's `TimeToAssign` reflects the full
        // redo round-trip, mirroring how the dialog measures the initial assign.
        const startTimestamp = Date.now();
        try {
            // Mark the job before starting polling so the polling-success handler skips the duplicate
            // trackChanges call. The fresh entity returned below is what lands on the undo stack.
            setAssignOpenShiftsJob({
                jobId: undoData.tagId, // best-effort placeholder until the real jobId comes back
                tagId: undoData.tagId,
                teamId: undoData.teamId,
                status: JobStatus.InProgress,
                reassignParams,
                skipTrackChanges: true,
                startTimestamp
            });
            const response = await SmartScheduleDataService.generateScheduleWithHistory(undoData.teamId, undoData.tagId, reassignParams);
            const jobId: string = response?.data?.jobId || undoData.tagId;
            setAssignOpenShiftsJob({
                jobId,
                tagId: undoData.tagId,
                teamId: undoData.teamId,
                status: JobStatus.InProgress,
                reassignParams,
                skipTrackChanges: true,
                startTimestamp
            });
            AssignOpenShiftsPollingService.startPolling(undoData.teamId, undoData.tagId);
        } catch (e) {
            trace.warn(`assignOpenShifts redo: re-assign failed: ${e?.message}`);
            // The first setAssignOpenShiftsJob above already pinned activeJob to InProgress before
            // the await; on rejection the job never starts polling, so the button would stay disabled
            // and the Assigning toast would render forever if we left activeJob set. Clear it, surface
            // the failure toast (mirroring the polling-success failure branch), then re-throw so
            // performRedo's revertChanges chain doesn't push a phantom entity onto the undo stack.
            clearAssignOpenShiftsJob();
            setShowFailureToast(undoData.tagId, undoData.teamId);
            throw e;
        }
        return new ChangeEntity(null, null, ChangeSource.AssignOpenShifts, buildUndoTask(undoData, reassignParams), null, null);
    };
}
